import { Component, OnDestroy, OnInit } from '@angular/core';
import { LazyService } from '../../services/lazy.service';
import { AlertsService } from '../../services/alerts.service';
import { LoaderService } from '../../services/loader.service';
import { BaseService } from '../../shared/services/base.service';
import { RdDevice, RdMode, RdServiceService } from '../../services/rd-service.service';
import { DialogConfig } from '../dialog/dialog-config';
import { DialogRef } from '../dialog/dialog-ref';

declare var QRCode: any;

/**
 * ABDM biometric authentication for an NHCX pre-auth or claim beneficiary — one dialog, three
 * modes, chosen by the `authMode` the launcher passes in.
 *
 * The two paths differ in where the capture happens, which is why they cannot share a stage list:
 *
 * FACE — the capture happens on the beneficiary's phone. We show a QR, they scan it with the ABHA
 * app and complete the face scan there, so this is a human-paced wait, not a button press:
 *
 *   intro     -> BIOMETRIC_AUTH_INIT returns a txnId; we render <qrUrl> as a QR
 *   awaiting  -> poll BIOMETRIC_AUTH_STATUS until the scan lands, or time out
 *   captured  -> operator types the Aadhaar and mobile, we call BIOMETRIC_AUTH_VERIFY
 *   verified  -> the returned ABHA profile is shown back as confirmation
 *
 * FINGERPRINT / IRIS — the capture happens right here, on the operator's own machine, through the
 * UIDAI RD Service. There is nothing to poll and no Aadhaar to type:
 *
 *   intro       -> discover the scanner (RdServiceService), before spending a txnId on it
 *   device      -> the detected model and status are named, operator presses Capture
 *   capturing   -> BIOMETRIC_AUTH_INIT, then the scan, then BIOMETRIC_AUTH_VERIFY with the PID
 *   verified    -> as above
 *
 * Two things are typed or captured here, sent once, and cleared: the Aadhaar number (face) and the
 * PID block (fingerprint / iris). Neither is stored on this component beyond its verify call,
 * neither is ever put in a query string, and the backend persists neither.
 */
@Component({
    selector: 'app-biometric-auth',
    templateUrl: './biometric-auth.component.html',
    styleUrls: ['./biometric-auth.component.scss'],
    standalone: false
})
export class BiometricAuthComponent implements OnInit, OnDestroy {

  /** Poll cadence while waiting for the beneficiary to finish the scan on their phone. */
  private static readonly POLL_INTERVAL_MS = 5000;
  /** Give up after this long rather than polling forever when the beneficiary walks away. */
  private static readonly POLL_TIMEOUT_MS = 5 * 60 * 1000;

  private static readonly APPLICATION = 'insurance';
  private static readonly RESOURCE = 'nhcx_biometric_auth';
  private static readonly QR_SCRIPT = './assets/js/qr-code/generate-qr-code.min.js';

  data: any = {};

  /** intro | awaiting | captured | device | capturing | verified | failed */
  stage = 'intro';

  /** FACE | FINGERPRINT | IRIS — set once in ngOnInit and never reassigned. */
  authMode = 'FACE';

  biometricAuth: any = {};
  aadhaar = '';
  mobile = '';

  /** Whether the Aadhaar field is unmasked. Never persists past a cleared Aadhaar. */
  aadhaarVisible = false;

  /**
   * Optional document filed against this cycle, offered only on a cyclic capture.
   *
   * The pre-auth and claim authentications already have their own consent-form fields on their
   * forms; a treatment cycle has no form of its own, so this dialog is the only place its
   * evidence can be attached. It is deliberately optional - a cycle stands on its biometric, and
   * this supplements it rather than substituting for it.
   */
  cyclicDoc = '';
  /** Minimal question object the uploader reads its own config from. */
  cyclicDocQuestion: any = { key: 'cyclic_doc', resource: 'orgImage' };
  /**
   * Title for `cyclicDoc`, auto-filled from the uploaded file's name and then the operator's to
   * edit. Sent as the document's Attachment.title on the claim; blank falls back to the file name.
   */
  cyclicDocTitle = '';

  /**
   * The period this session ran, as the picker holds it — `{startDate, endDate}` of dayjs objects.
   * Converted to millis only on the way out, so nothing here depends on the picker's own types.
   *
   * Separate from the treatment date: the date is the day the beneficiary was authenticated and is
   * what PAYR-1367 is judged on, this is how long the session took. **Mandatory** — it becomes the
   * `timingPeriod` of the cycle's supportingInfo entry on the claim.
   */
  cyclicPeriod: any = null;
  errorMessage = '';
  polling = false;
  timedOut = false;

  /** The scanner discovered on this workstation (fingerprint / iris only). */
  device: RdDevice = null;
  /** True while discovery or capture is in flight, so the template can show what is happening. */
  busy = false;
  busyMessage = '';

  private pollTimer: any = null;
  private timeoutTimer: any = null;
  private destroyed = false;

  /**
   * Re-read every second so the countdowns below actually move. zone.js patches setInterval, so
   * the tick is what drives change detection for the time labels.
   */
  now = Date.now();
  private tickTimer: any = null;
  /** When ABDM opened this transaction — shown so the operator can quote it in a support call. */
  initiatedAt = 0;
  /** Wall-clock deadline for the beneficiary to finish the scan on their phone (face only). */
  private waitDeadline = 0;

  constructor(
    private dialogRef: DialogRef,
    private dialogConfig: DialogConfig,
    private baseService: BaseService,
    private lazy: LazyService,
    private loader: LoaderService,
    private alerts: AlertsService,
    private rdService: RdServiceService
  ) { }

  ngOnInit(): void {
    // Opened from a decorator (DialogService.open) or from a capability selector
    // (DynamicPopUpComponent) — both land the context on dialogConfig.
    const config: any = this.dialogConfig || {};
    this.data = config.data || config['item'] || {};
    this.mobile = this.data.mobile || '';
    this.authMode = this.normalizeMode(this.data.authMode || this.data.authentication_type_code);
  }

  /** True when this dialog was opened to authenticate one cycle of a cyclic procedure. */
  get isCycleCapture(): boolean {
    return Number(this.data?.cycleNumber) > 0;
  }

  /** True for the two modes that capture locally through the RD Service. */
  get isLocalCapture(): boolean {
    return this.authMode !== 'FACE';
  }

  get modeLabel(): string {
    switch (this.authMode) {
      case 'FINGERPRINT': return 'Fingerprint';
      case 'IRIS': return 'IRIS Scan';
      default: return 'Face Authentication';
    }
  }

  private normalizeMode(mode: string): string {
    const probe = String(mode || '').toUpperCase();
    if (probe.indexOf('FINGER') === 0) {
      return 'FINGERPRINT';
    }
    if (probe.indexOf('IRIS') === 0) {
      return 'IRIS';
    }
    return 'FACE';
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPolling();
    this.stopTicker();
  }

  /**
   * Records that ABDM has opened a transaction and starts the clock. Called from both paths, so
   * the txnId and its timings appear the moment there is a txnId to show — not only once verified.
   */
  private noteInitialised(record: any): void {
    this.initiatedAt = (record && record.initDate) ? record.initDate : Date.now();
    this.startTicker();
  }

  private startTicker(): void {
    if (this.tickTimer) {
      return;
    }
    this.now = Date.now();
    this.tickTimer = setInterval(() => { this.now = Date.now(); }, 1000);
  }

  private stopTicker(): void {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  // ---------------------------------------------------------------- step 1: init

  /**
   * Entry point for both paths. Face goes straight to ABDM; fingerprint and iris look for the
   * scanner first, so a missing device costs nothing rather than burning a txnId that can never
   * be completed.
   */
  startAuthentication(): void {
    this.errorMessage = '';
    this.timedOut = false;
    if (this.isLocalCapture) {
      this.discoverDevice();
      return;
    }
    this.startFaceAuthentication();
  }

  /** Opens the authentication and renders the QR the beneficiary scans. */
  private startFaceAuthentication(): void {
    const body = {
      linkType: this.data.linkType,
      preAuthId: this.data.preAuthId || '',
      preAuthReferenceNumber: this.data.preAuthReferenceNumber || '',
      claimId: this.data.claimId || '',
      claimNumber: this.data.claimNumber || '',
      payerCode: this.data.payerCode || '',
      payerName: this.data.payerName || '',
      payerId: this.data.payerId || '',
      sender_code: this.data.sender_code || '',
      receipt_code: this.data.receipt_code || '',
      receipt_name: this.data.receipt_name || '',
      productId: this.data.productId || '',
      uhId: this.data.uhId || '',
      encounter_id: this.data.encounter_id || '',
      process: this.data.process || '',
      authMode: this.authMode,
      // Empty for an ordinary case-level authentication; set only when this capture covers one
      // cycle of a cyclic procedure, which is what the payer's PAYR-1367 lookup is judged on.
      procedureRef: this.data.procedureRef || '',
      cycleNumber: this.data.cycleNumber || 0
    };

    this.loader.showSpinner();
    this.baseService
      .action(body, 'BIOMETRIC_AUTH_INIT', BiometricAuthComponent.APPLICATION, BiometricAuthComponent.RESOURCE, false)
      .subscribe(
        (res: any) => {
          this.loader.hideSpinner();
          const record = this.firstResource(res);
          if (!record || !record.txnId) {
            this.fail(res && res.message ? res.message : 'Face authentication could not be started.');
            return;
          }
          this.biometricAuth = record;
          this.stage = 'awaiting';
          this.renderQr(record.qrUrl);
          this.startPolling();
        },
        (err: any) => {
          this.loader.hideSpinner();
          this.fail(this.messageOf(err, 'Face authentication could not be started.'));
        }
      );
  }

  // ------------------------------------------------- fingerprint / iris: local capture

  /**
   * Finds the scanner and names it, so the operator sees which device is about to be used before
   * a beneficiary is asked to put a finger on anything.
   */
  discoverDevice(): void {
    this.errorMessage = '';
    this.busy = true;
    this.busyMessage = 'Looking for a biometric scanner on this machine…';
    this.rdService.discover(this.authMode as RdMode)
      .then((device: RdDevice) => {
        this.busy = false;
        this.busyMessage = '';
        if (this.destroyed) {
          return;
        }
        this.device = device;
        this.stage = 'device';
      })
      .catch((e: any) => {
        this.busy = false;
        this.busyMessage = '';
        if (this.destroyed) {
          return;
        }
        // A device problem is not a dead end — the operator can plug one in and press retry,
        // so this stays on the intro stage rather than falling through to `failed`.
        this.device = null;
        this.errorMessage = this.textOf(e, 'No biometric scanner could be found on this machine.');
        this.stage = 'intro';
      });
  }

  /**
   * The whole local path in one operator gesture: open the transaction, capture the PID block,
   * verify it. Init is deliberately inside this rather than in discovery — a txnId is only spent
   * once there is a device ready to produce a capture for it.
   */
  captureAndVerify(): void {
    if (!this.device) {
      this.discoverDevice();
      return;
    }
    this.errorMessage = '';
    this.stage = 'capturing';
    this.busy = true;
    this.busyMessage = 'Starting the authentication…';

    this.initForCapture()
      .then((record: any) => {
        this.biometricAuth = record;
        this.noteInitialised(record);
        this.busyMessage = this.authMode === 'IRIS'
          ? 'Ask the beneficiary to look into the scanner…'
          : 'Ask the beneficiary to place their finger on the scanner…';
        return this.rdService.capture(this.device, this.authMode as RdMode);
      })
      .then((pid: string) => {
        this.busyMessage = 'Verifying the capture with ABDM…';
        return this.verifyCapture(pid);
      })
      .then((record: any) => {
        this.busy = false;
        this.busyMessage = '';
        if (this.destroyed) {
          return;
        }
        this.biometricAuth = record;
        this.stage = 'verified';
        this.alerts.success(this.modeLabel + ' authentication verified.');
      })
      .catch((e: any) => {
        this.busy = false;
        this.busyMessage = '';
        if (this.destroyed) {
          return;
        }
        // Back to the device stage, not `failed`: the device is still there and a re-scan is
        // usually all that is needed — a poorly placed finger is the common case.
        this.errorMessage = this.textOf(e, this.modeLabel + ' authentication could not be completed.');
        this.stage = 'device';
      });
  }

  /** BIOMETRIC_AUTH_INIT for the local path — ABHA number as loginId, no QR. */
  private initForCapture(): Promise<any> {
    const abhaNumber = this.data.abhaNumber || this.biometricAuth.abhaNumber || '';
    if (!abhaNumber) {
      return Promise.reject(new Error(
        'An ABHA number is required for ' + this.modeLabel + ' authentication. '
        + 'Enter it on the form before authenticating.'));
    }
    const body = {
      linkType: this.data.linkType,
      authMode: this.authMode,
      abhaNumber: abhaNumber,
      preAuthId: this.data.preAuthId || '',
      preAuthReferenceNumber: this.data.preAuthReferenceNumber || '',
      claimId: this.data.claimId || '',
      claimNumber: this.data.claimNumber || '',
      payerCode: this.data.payerCode || '',
      payerName: this.data.payerName || '',
      payerId: this.data.payerId || '',
      sender_code: this.data.sender_code || '',
      receipt_code: this.data.receipt_code || '',
      receipt_name: this.data.receipt_name || '',
      productId: this.data.productId || '',
      uhId: this.data.uhId || '',
      encounter_id: this.data.encounter_id || '',
      process: this.data.process || '',
      procedureRef: this.data.procedureRef || '',
      cycleNumber: this.data.cycleNumber || 0
    };
    return this.actionPromise(body, 'BIOMETRIC_AUTH_INIT',
      this.modeLabel + ' authentication could not be started.', (record: any) => !!record.txnId);
  }

  /** BIOMETRIC_AUTH_VERIFY carrying the captured PID block. */
  private verifyCapture(pid: string): Promise<any> {
    const body: any = {
      txnId: this.biometricAuth.txnId,
      authMode: this.authMode,
      bioPid: pid,
      rdServiceInfo: this.deviceDescription(),
      mobile: this.mobile || '',
      payerId: this.data.payerId || this.biometricAuth.payerId || '',
      sender_code: this.data.sender_code || this.biometricAuth.sender_code || '',
      receipt_code: this.data.receipt_code || this.biometricAuth.receipt_code || '',
      receipt_name: this.data.receipt_name || this.biometricAuth.receipt_name  || '',
      productId: this.data.productId || this.biometricAuth.productId || '',
    };
    return this.actionPromise(body, 'BIOMETRIC_AUTH_VERIFY',
      this.modeLabel + ' authentication could not be verified.',
      (record: any) => this.isVerified(record))
      // The PID is dropped from the request object as soon as the call is done, whichever way
      // it went, so it is not left sitting in a closure for the life of the dialog.
      .then((record: any) => { body.bioPid = ''; return record; },
            (e: any) => { body.bioPid = ''; throw e; });
  }

  /** Model, serial and RD Service version — the audit trail for which scanner was used. */
  private deviceDescription(): string {
    if (!this.device) {
      return '';
    }
    const parts = [];
    if (this.device.model) { parts.push('model=' + this.device.model); }
    if (this.device.serial) { parts.push('serial=' + this.device.serial); }
    if (this.device.rdsVer) { parts.push('rdsVer=' + this.device.rdsVer); }
    if (this.device.info) { parts.push('info=' + this.device.info); }
    parts.push('port=' + this.device.port);
    return parts.join('; ');
  }

  /**
   * A BaseService action as a promise, so the local path reads as the linear sequence it actually
   * is. `accept` decides whether the returned record represents success.
   */
  private actionPromise(body: any, action: string, failureMessage: string,
                        accept: (record: any) => boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      this.baseService
        .action(body, action, BiometricAuthComponent.APPLICATION, BiometricAuthComponent.RESOURCE, false)
        .subscribe(
          (res: any) => {
            const record = this.firstResource(res);
            if (!record || !accept(record)) {
              const message = (record && record.errorMessage)
                ? record.errorMessage
                : ((res && res.message) ? res.message : failureMessage);
              reject(new Error(message));
              return;
            }
            resolve(record);
          },
          (err: any) => reject(new Error(this.messageOf(err, failureMessage)))
        );
    });
  }

  /** Re-runs discovery after the operator has plugged the device in or changed it. */
  retryDiscovery(): void {
    this.rdService.forgetPort();
    this.device = null;
    this.errorMessage = '';
    this.discoverDevice();
  }

  // ---------------------------------------------------------------- step 2: poll

  private startPolling(): void {
    this.stopPolling();
    this.polling = true;
    this.waitDeadline = Date.now() + BiometricAuthComponent.POLL_TIMEOUT_MS;
    this.pollTimer = setInterval(() => this.checkStatus(true), BiometricAuthComponent.POLL_INTERVAL_MS);
    // A beneficiary who never scans must not leave this dialog polling indefinitely.
    this.timeoutTimer = setTimeout(() => {
      this.stopPolling();
      this.timedOut = true;
      this.errorMessage = 'The beneficiary did not complete the face scan in time. Start again to issue a new QR.';
    }, BiometricAuthComponent.POLL_TIMEOUT_MS);
  }

  private stopPolling(): void {
    this.polling = false;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  /** One capture/pid poll. `silent` suppresses the spinner so the background loop is not jarring. */
  checkStatus(silent?: boolean): void {
    if (this.destroyed || !this.biometricAuth || !this.biometricAuth.txnId) {
      return;
    }
    if (!silent) {
      this.loader.showSpinner();
    }
    this.baseService
      .action({ txnId: this.biometricAuth.txnId }, 'BIOMETRIC_AUTH_STATUS',
        BiometricAuthComponent.APPLICATION, BiometricAuthComponent.RESOURCE, false)
      .subscribe(
        (res: any) => {
          if (!silent) {
            this.loader.hideSpinner();
          }
          const record = this.firstResource(res);
          if (!record) {
            return;
          }
          this.biometricAuth = record;
          if (record.status === 'CAPTURED' || this.isVerified(record)) {
            this.stopPolling();
            // The Aadhaar belongs to whoever was just scanned, so the field opens empty even if a
            // previous attempt in this dialog left something behind.
            this.aadhaar = '';
            this.aadhaarVisible = false;
            this.stage = this.isVerified(record) ? 'verified' : 'captured';
          } else if (record.status === 'FAILED') {
            this.stopPolling();
            this.fail(record.errorMessage || 'The face scan failed. Start again to issue a new QR.');
          }
        },
        (err: any) => {
          if (!silent) {
            this.loader.hideSpinner();
            this.alerts.error(this.messageOf(err, 'Could not read the face-scan status.'));
          }
          // A silent poll failure is left alone: the next tick retries, and the hard timeout
          // still ends the wait if the transaction is genuinely dead.
        }
      );
  }

  // ---------------------------------------------------------------- step 3: verify

  /** Sends the operator-typed Aadhaar for verification, then clears it from this component. */
  verify(): void {
    if (!this.isAadhaarValid()) {
      this.alerts.error('Enter the beneficiary\'s 12-digit Aadhaar number.');
      return;
    }
    const body = {
      txnId: this.biometricAuth.txnId,
      aadhaar: this.aadhaar.replace(/\s/g, ''),
      mobile: this.mobile || '',
      payerId: this.data.payerId || this.biometricAuth.payerId || ''
    };
    // Cleared before the response lands, so it is not sitting in component state during the call.
    this.aadhaar = '';
    this.aadhaarVisible = false;

    this.loader.showSpinner();
    this.baseService
      .action(body, 'BIOMETRIC_AUTH_VERIFY', BiometricAuthComponent.APPLICATION, BiometricAuthComponent.RESOURCE, false)
      .subscribe(
        (res: any) => {
          this.loader.hideSpinner();
          body.aadhaar = '';
          const record = this.firstResource(res);
          if (!record || !this.isVerified(record)) {
            this.errorMessage = (record && record.errorMessage)
              ? record.errorMessage
              : (res && res.message ? res.message : 'Face authentication could not be verified.');
            return;
          }
          this.biometricAuth = record;
          this.stage = 'verified';
          this.alerts.success('Face authentication verified.');
        },
        (err: any) => {
          this.loader.hideSpinner();
          body.aadhaar = '';
          this.errorMessage = this.messageOf(err, 'Face authentication could not be verified.');
        }
      );
  }

  // ---------------------------------------------------------------- close / retry

  /**
   * Hands the verified txnId back to the form that opened this dialog.
   *
   * Refuses once the token has expired: the form would persist a txnId the submission gate is
   * about to reject, and the operator would learn of it only from a payer rejection.
   */
  done(): void {
    if (this.tokenExpired) {
      this.errorMessage = 'This authentication has expired. Authenticate the beneficiary again.';
      return;
    }
    // The treatment period is what becomes the cycle's timingPeriod on the claim, so a cycle
    // cannot be recorded without one. Refused here as well as on the server so the operator is
    // told before the authentication is spent, not after.
    if (this.isCycleCapture) {
      const period = this.periodAsMillis();
      if (!period || !period.startDate || !period.endDate) {
        this.errorMessage = 'Select the treatment period for this cycle before continuing.';
        return;
      }
      if (period.endDate < period.startDate) {
        this.errorMessage = 'The treatment period ends before it starts.';
        return;
      }
    }
    this.stopPolling();
    this.stopTicker();
    this.dialogRef.close({
      txnId: this.biometricAuth.txnId,
      status: this.biometricAuth.status,
      // The document stays optional; the period is guaranteed present by the guard above.
      cyclic_doc: this.cyclicDoc || '',
      cyclic_doc_title: this.cyclicDoc ? (this.cyclicDocTitle || '').trim() : '',
      service_validity: this.periodAsMillis(),
      authMode: this.authMode,
      authentication_type_code: this.typeCode(),
      authentication_type_display: this.modeLabel,
      abhaNumber: this.verifiedAbhaNumber,
      patient_name: this.verifiedName,
      tokenExpiresAt: this.biometricAuth.tokenExpiresAt,
      verifiedDate: this.biometricAuth.verifiedDate
    });
  }

  /** A fresh upload replaces whatever title the previous file had. */
  onCyclicDocUploaded(event: any): void {
    const name = event?.data?.documentName || event?.fileInfo?.name;
    if (name) {
      this.cyclicDocTitle = name;
    }
  }

  /** A title without its document would label nothing. */
  onCyclicDocRemoved(): void {
    this.cyclicDocTitle = '';
  }

  /**
   * The picked period as the epoch-millis pair the schema stores, or null when nothing was picked.
   *
   * The picker hands back whatever date type it was built on, so this goes through `valueOf()`
   * rather than assuming dayjs, a Date or a string — and a value that cannot produce a finite
   * number is dropped instead of being stored as NaN.
   */
  private periodAsMillis(): any {
    const start = this.millisOf(this.cyclicPeriod?.startDate);
    const end = this.millisOf(this.cyclicPeriod?.endDate);
    if (!start && !end) {
      return null;
    }
    return { startDate: start, endDate: end };
  }

  private millisOf(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const millis = typeof value?.valueOf === 'function' ? Number(value.valueOf()) : Number(value);
    return isFinite(millis) ? millis : 0;
  }

  cancel(): void {
    this.stopPolling();
    this.dialogRef.close();
  }

  /**
   * Abandons the current transaction and issues a fresh one. The discovered device is kept: it is
   * still plugged in, and re-scanning ports on every retry is the behaviour this deliberately
   * avoids. `retryDiscovery()` is the way to look again.
   */
  restart(): void {
    this.stopPolling();
    this.biometricAuth = {};
    this.aadhaar = '';
    this.aadhaarVisible = false;
    this.initiatedAt = 0;
    this.waitDeadline = 0;
    this.stopTicker();
    this.stage = this.isLocalCapture && this.device ? 'device' : 'intro';
    this.errorMessage = '';
    this.timedOut = false;
    this.busy = false;
    this.busyMessage = '';
  }

  /** The `authentication_type_code` this mode writes back onto the form. */
  private typeCode(): string {
    switch (this.authMode) {
      case 'FINGERPRINT': return 'fingerprint';
      case 'IRIS': return 'iris';
      default: return 'face';
    }
  }

  isAadhaarValid(): boolean {
    return /^\d{12}$/.test((this.aadhaar || '').replace(/\s/g, ''));
  }

  /**
   * The beneficiary's name and ABHA number for the verified stage.
   *
   * The backend stores the whole ABDM response under `abhaProfile` but no longer lifts these two
   * out onto their own fields, so prefer whatever the record does carry and otherwise fall back to
   * what the form already knew. For fingerprint and iris the ABHA number is always known — it is
   * the `loginId` the authentication was opened with.
   */
  get verifiedName(): string {
    return this.biometricAuth.patient_name || this.data.patient_name || '';
  }

  get verifiedAbhaNumber(): string {
    return this.biometricAuth.abhaNumber || this.data.abhaNumber || '';
  }

  /** Minutes left on the biometric token, for the operator to see before submitting. */
  get minutesRemaining(): number {
    if (!this.biometricAuth || !this.biometricAuth.tokenExpiresAt) {
      return 0;
    }
    return Math.max(0, Math.floor((this.biometricAuth.tokenExpiresAt - this.now) / 60000));
  }

  /** True once there is a transaction worth showing the operator. */
  get hasTransaction(): boolean {
    return !!(this.biometricAuth && this.biometricAuth.txnId);
  }

  /** Clock time this transaction was opened, e.g. "14:32:07". */
  get startedAtLabel(): string {
    return this.initiatedAt ? new Date(this.initiatedAt).toLocaleTimeString() : '';
  }

  /** Clock time ABDM issued the token, e.g. "14:33:12". */
  get verifiedAtLabel(): string {
    const at = this.biometricAuth ? this.biometricAuth.verifiedDate : 0;
    return at ? new Date(at).toLocaleTimeString() : this.startedAtLabel;
  }

  /** How long this transaction has been open, counting up — mm:ss. */
  get elapsedLabel(): string {
    return this.initiatedAt ? this.clock(this.now - this.initiatedAt) : '';
  }

  /**
   * How long the beneficiary still has to finish the scan on their phone before this dialog
   * stops waiting — mm:ss. Face only; the local capture paths have no such wait.
   */
  get waitRemainingLabel(): string {
    return this.waitDeadline ? this.clock(this.waitDeadline - this.now) : '';
  }

  /** How long the issued biometric token is still good for — mm:ss, ticking down. */
  get validForLabel(): string {
    if (!this.biometricAuth || !this.biometricAuth.tokenExpiresAt) {
      return '';
    }
    return this.clock(this.biometricAuth.tokenExpiresAt - this.now);
  }

  /** True once the issued token is past its expiry, so the operator is told to redo it. */
  get tokenExpired(): boolean {
    return !!(this.biometricAuth && this.biometricAuth.tokenExpiresAt
      && this.now >= this.biometricAuth.tokenExpiresAt);
  }

  /** Milliseconds as mm:ss, floored at zero — never a negative countdown. */
  private clock(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(total / 60);
    const ss = total % 60;
    return mm + ':' + (ss < 10 ? '0' : '') + ss;
  }

  // ---------------------------------------------------------------- internals

  /**
   * Renders the ABDM face-auth URL as a QR. Reuses the generator already vendored for
   * app-qr-code-generator rather than adding a package — the existing qr-code-reader component is
   * a camera scanner and cannot draw one.
   */
  private renderQr(qrUrl: string): void {
    if (!qrUrl) {
      this.fail('ABDM did not return a scan URL for this authentication.');
      return;
    }
    this.lazy.loadScript(BiometricAuthComponent.QR_SCRIPT).then(() => {
      // The container only exists once the awaiting template has rendered.
      setTimeout(() => {
        const holder = document.getElementById('biometric-auth-qr');
        if (!holder || this.destroyed) {
          return;
        }
        holder.innerHTML = '';
        new QRCode(holder, qrUrl);
      });
    }).catch(() => {
      this.fail('The QR generator could not be loaded.');
    });
  }

  private fail(message: string): void {
    this.stopPolling();
    this.stage = 'failed';
    this.errorMessage = message;
  }

  private firstResource(res: any): any {
    if (res && res.resource && res.resource.length > 0) {
      return res.resource[0];
    }
    return null;
  }

  /**
   * True once the backend has a token for this transaction.
   *
   * Both spellings are accepted deliberately. `BiometricAuthUtil` writes `STATUS_COMPLETE`
   * ("COMPLETE") on a successful verify and gates submission on that same constant, while the
   * resource schema documents the state as "VERIFIED". Testing only for VERIFIED made a
   * successful verify look like a failure in this dialog — a defect that survived because only
   * the first stage is reachable without live ABDM.
   */
  private isVerified(record: any): boolean {
    const status = record ? record.status : '';
    return status === 'COMPLETE' || status === 'VERIFIED';
  }

  /** An Error thrown by the RD service or a rejected action, as operator-facing text. */
  private textOf(e: any, fallback: string): string {
    if (e && e.message) {
      return e.message;
    }
    return this.messageOf(e, fallback);
  }

  private messageOf(err: any, fallback: string): string {
    if (err && err.error && err.error.message) {
      return err.error.message;
    }
    if (err && err.message) {
      return err.message;
    }
    return fallback;
  }
}
